const DRAG_THRESHOLD = 6;

/**
 * A horizontal card slider built on a native scroll-snap track.
 *
 * The track is a real scroll container, so the cards are laid out by the
 * browser the moment the panel around it is revealed. Nothing here measures
 * the slider in order to position cards, which is what made the previous
 * Flickity build collapse when it was initialised inside a hidden panel. The
 * script only decides which controls to show and where a control should
 * scroll to.
 *
 * Every scroll is expressed as a physical delta measured from live bounding
 * boxes, so it is correct in both RTL and LTR without depending on which sign
 * convention the browser uses for scrollLeft on an RTL container.
 */
class TerminalSlider extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();

    this.track = this.querySelector('[data-slider-track]');
    if (!this.track) return;

    this.prevButton = this.querySelector('[data-slider-prev]');
    this.nextButton = this.querySelector('[data-slider-next]');
    this.dotsWrapper = this.querySelector('[data-slider-dots]');
    this.cells = [...this.track.querySelectorAll(':scope > [data-slide]')];
    this.dots = [];
    this.perView = 0;
    this.pageCount = 0;

    if (!this.cells.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.prevButton?.addEventListener('click', () => this.step(-1), { signal });
    this.nextButton?.addEventListener('click', () => this.step(1), { signal });

    // Browsers coalesce scroll events to one per frame, so this reads layout
    // once per frame. Batching it behind requestAnimationFrame instead would
    // leave the controls stale wherever frames are throttled.
    this.track.addEventListener('scroll', () => this.update(), { passive: true, signal });
    this.track.addEventListener('pointerdown', (event) => this.onPointerDown(event), { signal });
    this.track.addEventListener('dragstart', (event) => event.preventDefault(), { signal });
    this.track.addEventListener('click', (event) => this.onClick(event), { capture: true, signal });

    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

    this.onPointerMoveBound = (event) => this.onPointerMove(event);
    this.onPointerUpBound = (event) => this.onPointerUp(event);

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', () => this.refresh(), { signal });
    } else {
      // The first card is watched as well as the track: a breakpoint can
      // change how many cards per view without changing the track's width.
      this.resizeObserver = new ResizeObserver(() => this.refresh());
      this.resizeObserver.observe(this.track);
      this.resizeObserver.observe(this.cells[0]);
    }

    this.refresh();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.dragController?.abort();
    this.dragController = null;
    clearTimeout(this.suppressClickTimer);
    clearTimeout(this.settleTimer);
    this.resizeObserver?.disconnect();
    this.resizeObserver = null;
  }

  get isRtl() {
    return getComputedStyle(this.track).direction === 'rtl';
  }

  get prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /**
   * Re-derive how many cards fit, how many pages that makes, and which
   * controls belong on screen. Safe to call at any time, including while the
   * slider is still hidden, where it simply keeps the controls away.
   */
  refresh() {
    const viewport = this.track.clientWidth;
    const cardWidth = this.cells[0].getBoundingClientRect().width;

    if (!viewport || !cardWidth) {
      this.showControls(false);
      return;
    }

    const gap = parseFloat(getComputedStyle(this.track).columnGap) || 0;
    // CSS owns the breakpoints, so read the declared value rather than inferring
    // it: once the cards carry a peek the measured ratio is a rounding gamble.
    const declared = parseFloat(getComputedStyle(this).getPropertyValue('--cards-per-view'));
    const perView = Number.isFinite(declared) && declared >= 1
      ? Math.round(declared)
      : Math.max(1, Math.round((viewport + gap) / (cardWidth + gap)));
    const pageCount = Math.max(1, Math.ceil(this.cells.length / perView));

    if (perView !== this.perView || pageCount !== this.pageCount) {
      this.perView = perView;
      this.pageCount = pageCount;
      this.buildDots();
    }

    // Drives --card-peek. The peek is only wanted when there is a next card to
    // show a sliver of, otherwise it would just leave the row short.
    this.classList.toggle('is-scrollable', pageCount > 1);
    this.showControls(pageCount > 1);
    this.update();
  }

  showControls(visible) {
    if (this.prevButton) this.prevButton.hidden = !visible;
    if (this.nextButton) this.nextButton.hidden = !visible;
    if (this.dotsWrapper) this.dotsWrapper.hidden = !visible;
  }

  buildDots() {
    if (!this.dotsWrapper) return;

    this.dotsWrapper.textContent = '';
    this.dots = [];

    if (this.pageCount < 2) return;

    const labelTemplate = this.dataset.pageLabel || '__PAGE__';

    for (let page = 0; page < this.pageCount; page += 1) {
      const dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'terminal-hub__dot';
      dot.setAttribute('aria-label', labelTemplate.replace('__PAGE__', String(page + 1)));
      dot.addEventListener('click', () => this.goToCell(this.pageStart(page)));
      this.dotsWrapper.appendChild(dot);
      this.dots.push(dot);
    }
  }

  /**
   * First card of a page. The last page is pulled back so the row ends flush
   * with the track instead of trailing a half-empty page.
   */
  pageStart(page) {
    return Math.max(0, Math.min(page * this.perView, this.cells.length - this.perView));
  }

  /** Physical pixels to scroll so that `cell` sits against the inline start. */
  deltaTo(cell) {
    const track = this.track.getBoundingClientRect();
    const box = cell.getBoundingClientRect();
    return this.isRtl ? box.right - track.right : box.left - track.left;
  }

  /** The card currently closest to the inline start of the track. */
  currentIndex() {
    const rtl = this.isRtl;
    const track = this.track.getBoundingClientRect();
    const edge = rtl ? track.right : track.left;
    let closest = 0;
    let shortest = Infinity;

    this.cells.forEach((cell, index) => {
      const box = cell.getBoundingClientRect();
      const distance = Math.abs((rtl ? box.right : box.left) - edge);
      if (distance < shortest - 0.5) {
        shortest = distance;
        closest = index;
      }
    });

    return closest;
  }

  currentPage() {
    const index = this.currentIndex();
    let page = 0;
    let shortest = Infinity;

    for (let candidate = 0; candidate < this.pageCount; candidate += 1) {
      const distance = Math.abs(this.pageStart(candidate) - index);
      if (distance < shortest) {
        shortest = distance;
        page = candidate;
      }
    }

    return page;
  }

  goToCell(index, behavior) {
    const cell = this.cells[Math.max(0, Math.min(index, this.cells.length - 1))];
    if (!cell) return;

    const delta = this.deltaTo(cell);
    if (Math.abs(delta) < 1) return;

    // 'auto' would defer to the track's CSS scroll-behavior, which is smooth,
    // so an instant jump has to say 'instant'.
    this.track.scrollTo({
      left: this.track.scrollLeft + delta,
      behavior: behavior || (this.prefersReducedMotion ? 'instant' : 'smooth'),
    });
  }

  /**
   * Move one viewport of cards. Overshooting the end is clamped by the
   * browser, which lands on the flush-end page, so nothing to clamp here.
   */
  step(direction) {
    this.goToCell(this.currentIndex() + direction * this.perView);
  }

  update() {
    if (!this.cells.length) return;

    const page = this.currentPage();
    this.dots.forEach((dot, index) => {
      const isActive = index === page;
      dot.classList.toggle('is-active', isActive);
      if (isActive) {
        dot.setAttribute('aria-current', 'true');
      } else {
        dot.removeAttribute('aria-current');
      }
    });

    this.centreDotsOnActive();

    const rtl = this.isRtl;
    const track = this.track.getBoundingClientRect();
    const first = this.cells[0].getBoundingClientRect();
    const last = this.cells[this.cells.length - 1].getBoundingClientRect();
    const atStart = rtl ? first.right <= track.right + 1 : first.left >= track.left - 1;
    const atEnd = rtl ? last.left >= track.left - 1 : last.right <= track.right + 1;

    if (this.prevButton) this.prevButton.disabled = atStart;
    if (this.nextButton) this.nextButton.disabled = atEnd;
  }

  /**
   * Slide the dot row so the current page's marker sits on the centre line
   * rather than the row as a whole. The active marker is a wide pill and the
   * rest are small dots, so centring the row leaves the pill off to one side,
   * and to opposite sides in RTL and LTR.
   *
   * offsetLeft is used rather than a client rect because it reports layout
   * position, which this element's own transform does not move, so repeated
   * calls cannot compound.
   */
  centreDotsOnActive() {
    if (!this.dotsWrapper) return;

    const active = this.dots.find((dot) => dot.classList.contains('is-active'));
    if (!active || this.dots.length < 2) {
      this.dotsWrapper.style.removeProperty('--dots-shift');
      return;
    }

    const starts = this.dots.map((dot) => dot.offsetLeft);
    const ends = this.dots.map((dot) => dot.offsetLeft + dot.offsetWidth);
    const rowCentre = (Math.min(...starts) + Math.max(...ends)) / 2;
    const activeCentre = active.offsetLeft + active.offsetWidth / 2;

    // Capped at half a dot pitch. That is exactly the correction a two dot row
    // needs, and it stops a long row from being dragged off centre chasing an
    // active marker that is several dots from the middle.
    const gap = parseFloat(getComputedStyle(this.dotsWrapper).columnGap) || 0;
    const limit = (Math.min(...this.dots.map((dot) => dot.offsetWidth)) + gap) / 2;
    const shift = Math.max(-limit, Math.min(limit, rowCentre - activeCentre));

    this.dotsWrapper.style.setProperty('--dots-shift', `${Math.round(shift)}px`);
  }

  onKeydown(event) {
    if (event.target instanceof HTMLInputElement || event.target instanceof HTMLTextAreaElement) return;

    const rtl = this.isRtl;
    let direction = 0;

    if (event.key === 'ArrowRight') direction = rtl ? -1 : 1;
    if (event.key === 'ArrowLeft') direction = rtl ? 1 : -1;

    if (direction) {
      event.preventDefault();
      this.step(direction);
      return;
    }

    if (event.key === 'Home') {
      event.preventDefault();
      this.goToCell(0);
    }

    if (event.key === 'End') {
      event.preventDefault();
      this.goToCell(this.cells.length - 1);
    }
  }

  // Touch and trackpad already scroll the track natively. This only adds
  // click and drag for a mouse.
  //
  // Deliberately no setPointerCapture: capturing the pointer retargets the
  // follow-up click to the track, which stops a plain click on a card from
  // opening its link. Listening on the window instead keeps the drag alive
  // when the cursor leaves the track and leaves the click alone.
  onPointerDown(event) {
    if (event.pointerType === 'touch' || event.button !== 0) return;

    this.suppressClick = false;
    this.isPointerDown = true;
    this.isDragging = false;
    this.dragPointerId = event.pointerId;
    this.dragLastX = event.clientX;
    this.dragDistance = 0;

    this.dragController?.abort();
    this.dragController = new AbortController();
    const options = { signal: this.dragController.signal };
    window.addEventListener('pointermove', this.onPointerMoveBound, options);
    window.addEventListener('pointerup', this.onPointerUpBound, options);
    window.addEventListener('pointercancel', this.onPointerUpBound, options);
  }

  onPointerMove(event) {
    if (!this.isPointerDown || event.pointerId !== this.dragPointerId) return;

    const movement = event.clientX - this.dragLastX;
    this.dragLastX = event.clientX;
    this.dragDistance += Math.abs(movement);

    // Stay out of the way until the pointer has moved far enough to mean it.
    if (!this.isDragging) {
      if (this.dragDistance <= DRAG_THRESHOLD) return;
      this.isDragging = true;
      this.track.classList.add('is-dragging');
    }

    // Physical axis, so this needs no direction handling.
    this.track.scrollLeft -= movement;
  }

  onPointerUp(event) {
    if (!this.isPointerDown || event.pointerId !== this.dragPointerId) return;

    this.isPointerDown = false;
    this.dragPointerId = null;
    this.dragController?.abort();
    this.dragController = null;

    if (!this.isDragging) return;

    const settleOn = this.currentIndex();
    this.isDragging = false;
    this.suppressClick = true;
    this.track.classList.remove('is-dragging');

    // The click that closes out this drag is dispatched before a zero delay
    // timer runs, so this clears the flag right after it without leaving it
    // armed for some later, unrelated click.
    clearTimeout(this.suppressClickTimer);
    this.suppressClickTimer = setTimeout(() => {
      this.suppressClick = false;
    }, 0);

    // is-settling holds mandatory snapping off while the settle scroll runs.
    // Restoring it now would make the browser re-snap immediately and cancel
    // the animation; restoring it once the scroll has landed is a no-op,
    // because by then the row is already sitting on a snap point.
    this.track.classList.add('is-settling');
    this.goToCell(settleOn);
    this.endSettle();
  }

  /**
   * Put snapping back once the settle scroll has finished. A settle that had
   * nowhere to go fires no scrollend at all, and scrollend is still missing in
   * some browsers, so the timer is the real guarantee and the event is the fast
   * path.
   */
  endSettle() {
    clearTimeout(this.settleTimer);

    const restore = () => {
      clearTimeout(this.settleTimer);
      this.track.removeEventListener('scrollend', restore);
      this.track.classList.remove('is-settling');
    };

    this.track.addEventListener('scrollend', restore, { once: true });
    this.settleTimer = setTimeout(restore, 600);
  }

  // A drag that ends on top of a card must not follow the card's link.
  onClick(event) {
    if (!this.suppressClick) return;

    this.suppressClick = false;
    event.preventDefault();
    event.stopPropagation();
  }
}

class TerminalHub extends HTMLElement {
  connectedCallback() {
    this.abortController?.abort();
    this.tabs = [...this.querySelectorAll('[data-terminal-tab]')];
    this.panels = [...this.querySelectorAll('[data-terminal-panel]')];
    this.closeButtons = [...this.querySelectorAll('[data-terminal-close]')];
    this.activeTerminal = null;

    if (!this.tabs.length || !this.panels.length) return;

    this.abortController = new AbortController();
    const { signal } = this.abortController;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', () => this.toggle(tab.dataset.terminalTab), { signal });
      tab.addEventListener('keydown', (event) => this.onKeydown(event), { signal });
    });

    this.closeButtons.forEach((button) => {
      button.addEventListener('click', () => this.close(button.dataset.terminalClose), { signal });
    });

    document.addEventListener('shopify:block:select', (event) => this.onBlockSelect(event), { signal });
    this.close();
  }

  disconnectedCallback() {
    this.abortController?.abort();
  }

  toggle(terminal) {
    if (this.activeTerminal === terminal) {
      this.close();
      return;
    }

    this.open(terminal);
  }

  open(terminal) {
    const activeTab = this.tabs.find((tab) => tab.dataset.terminalTab === terminal);
    const activePanel = this.panels.find((panel) => panel.dataset.terminalPanel === terminal);

    if (!activeTab || !activePanel) return null;

    this.activeTerminal = terminal;

    this.tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-expanded', String(isActive));
    });

    this.panels.forEach((panel) => {
      const isActive = panel === activePanel;
      panel.classList.toggle('is-open', isActive);
      panel.hidden = !isActive;
      panel.inert = !isActive;
      panel.setAttribute('aria-hidden', String(!isActive));
    });

    // The slider's own ResizeObserver also catches this, but refreshing here
    // means the controls are already right on the first painted frame.
    activePanel.querySelector('terminal-slider')?.refresh();

    return activePanel;
  }

  close(terminalToFocus = null) {
    this.activeTerminal = null;

    this.tabs.forEach((tab) => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-expanded', 'false');
    });

    this.panels.forEach((panel) => {
      panel.classList.remove('is-open');
      panel.hidden = true;
      panel.inert = true;
      panel.setAttribute('aria-hidden', 'true');
    });

    if (terminalToFocus) {
      this.tabs
        .find((tab) => tab.dataset.terminalTab === terminalToFocus)
        ?.focus({ preventScroll: true });
    }
  }

  onKeydown(event) {
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    const isRtl = getComputedStyle(this).direction === 'rtl';
    let nextIndex = currentIndex;

    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = this.tabs.length - 1;
    if (event.key === 'ArrowRight') nextIndex += isRtl ? -1 : 1;
    if (event.key === 'ArrowLeft') nextIndex += isRtl ? 1 : -1;

    if (nextIndex === currentIndex) return;

    event.preventDefault();
    nextIndex = (nextIndex + this.tabs.length) % this.tabs.length;
    this.tabs[nextIndex].focus({ preventScroll: true });
  }

  onBlockSelect(event) {
    if (!(event.target instanceof Element)) return;

    const panel = event.target.closest('[data-terminal-panel]');
    if (!panel || !this.contains(panel)) return;

    this.open(panel.dataset.terminalPanel);

    const slider = panel.querySelector('terminal-slider');
    const cell = event.target.closest('[data-slide]');
    if (!slider || !cell || !slider.cells?.includes(cell)) return;

    slider.goToCell(slider.cells.indexOf(cell), 'instant');
  }
}

if (!customElements.get('terminal-slider')) {
  customElements.define('terminal-slider', TerminalSlider);
}

if (!customElements.get('terminal-hub')) {
  customElements.define('terminal-hub', TerminalHub);
}
